import {getAppConfigVar} from '../config';
import {
	SprinklerTime,
	WaterAdviceParam,
	WateringAdviceLegendItem,
	WateringAdviceLegendItems,
	WateringAdviceMapData, WateringAdviceSubscriberData
} from '../../types/WateringAdviceMapData';
import {hexToRgbaObject} from '../../utils/color';
import {Library, Loader} from '@googlemaps/js-api-loader';
import {GoogleGeocodeInfo} from '../../types/Google/Geocode/Info';
import {v4 as uuidv4} from 'uuid';
import {GoogleMapsMouseupEvent} from '../../types/Google/Maps/EventListener';
import LatLng = google.maps.LatLng;


/**
 *
 */
let wateringAdviceApiUrl: string | null                     = null;
let addressLocatorInfoWindow: google.maps.InfoWindow | null = null;
let boundingBoxPoly: google.maps.Polygon | null             = null;
let initialBounds: google.maps.LatLngBounds | null          = null;
const mapOverlays: google.maps.OverlayView[]                = [];
const clickTrackers: string[]                               = [];


/**
 *
 */
const setupWeeklyWateringAdvice = () =>
{
	const sections = document.querySelectorAll( '.section-weekly-watering-advice' );

	wateringAdviceApiUrl = getAppConfigVar( 'wateringAdviceApiUrl' );

	if (typeof wateringAdviceApiUrl !== 'string' || !sections.length) {
		return;
	}

	fetchMapData().then( mapData =>
	{
		populateText( mapData );
		initMap( mapData ).then( map =>
		{
			showSelectedWaterView( mapData, map );
			setupWaterViewRadios( mapData, map );

			setTimeout( () =>
			{
				if (mapData.subscriber) {
					showAdviceForSubscriber( mapData, map, mapData.subscriber );
				}
			}, 500 );
		} );
	} );
};


/**
 *
 * @see https://github.com/googlemaps/js-api-loader
 */
const initMap = ( mapData: WateringAdviceMapData ): Promise<google.maps.Map> =>
{
	return new Promise( ( resolve ) =>
	{
		const mapContainer = document.querySelector( '#advice-map-canvas' );

		if (!(mapContainer instanceof HTMLElement)) {
			return;
		}

		const loader               = new Loader( {
			apiKey:  getAppConfigVar( 'googleMapsApiKey' ),
			version: 'weekly',
		} );
		const libraries: Library[] = ['core'];
		const promises             = libraries.map( name => loader.importLibrary( name ) );

		Promise.all( promises ).then( () =>
		{
			const sw = new google.maps.LatLng( mapData.map.bounds.sw.lat, mapData.map.bounds.sw.lng );
			const ne = new google.maps.LatLng( mapData.map.bounds.ne.lat, mapData.map.bounds.ne.lng );

			initialBounds = new google.maps.LatLngBounds( sw, ne );

			const center = new google.maps.LatLng( mapData.waterAdviceCenter.lat, mapData.waterAdviceCenter.lng );
			const map    = new google.maps.Map( mapContainer, {
				zoom:                     5,
				center:                   center,
				mapTypeControl:           false,
				streetViewControl:        false,
				zoomControl:              true,
				fullscreenControlOptions: {
					position: google.maps.ControlPosition.BOTTOM_LEFT
				},
			} );

			map.mapTypes.set( 'wia-style', new google.maps.StyledMapType( getMapStyles() ) );
			map.setMapTypeId( 'wia-style' );

			google.maps.event.addListenerOnce( map, 'idle', function ()
			{
				// keep zooming in until the overlay completely covers the map

				for (let zoom: number | undefined = map.getZoom(); zoom && zoom < 16; zoom++) {

					map.setZoom( zoom );

					const mapBounds = map.getBounds();

					if (mapBounds && initialBounds) {
						if (initialBounds.contains( mapBounds.getNorthEast() ) && initialBounds.contains( mapBounds.getSouthWest() )) {
							break;
						}
					}
				}
			} );

			// create bounding box

			boundingBoxPoly = new google.maps.Polygon( {
				paths:         [
					{ lat: mapData.map.bounds.nw.lat, lng: mapData.map.bounds.nw.lng },
					{ lat: mapData.map.bounds.ne.lat, lng: mapData.map.bounds.ne.lng },
					{ lat: mapData.map.bounds.se.lat, lng: mapData.map.bounds.se.lng },
					{ lat: mapData.map.bounds.sw.lat, lng: mapData.map.bounds.sw.lng }
				],
				strokeWeight:  1,
				strokeOpacity: 1,
				fillOpacity:   0,
				zIndex:        5,
				map:           map
			} );

			// create address/watering advice info window

			addressLocatorInfoWindow = new google.maps.InfoWindow();

			// add address locator

			setupAddressLocator( mapData, map );

			// responsive re-center

			window.addEventListener( 'resize', () =>
			{
				const currentCenter = map.getCenter();

				if (currentCenter) {
					google.maps.event.trigger( map, 'resize' );
					map.setCenter( currentCenter );
				}
			} );

			resolve( map );
		} );
	} );
};


/**
 *
 */
const showAdviceForSubscriber = (
	mapData: WateringAdviceMapData,
	map: google.maps.Map,
	subscriberData: WateringAdviceSubscriberData
) =>
{
	const addressInput = document.querySelector( '#wa-locator-address' );

	if (subscriberData.address && addressInput instanceof HTMLInputElement) {
		addressInput.value = subscriberData.address;
	}

	if (subscriberData.coords) {

		subscriberData.latLng = new google.maps.LatLng( subscriberData.coords.lat, subscriberData.coords.lng );

		showAdviceInfoWindowForLatLng( mapData, map, subscriberData.latLng, true );
	}
};

/**
 *
 */
const setupAddressLocator = ( mapData: WateringAdviceMapData, map: google.maps.Map ) =>
{
	const addressLocatorForm = document.createElement( 'form' );

	addressLocatorForm.name      = 'wa-locator-form';
	addressLocatorForm.id        = 'wa-locator-form';
	addressLocatorForm.innerHTML = `
		<input
			type="text"
			name="wa-locator-address"
			id="wa-locator-address"
			class="form-control"
			placeholder="Enter your address"
			aria-label="Enter your address"
		>
		<button
			type="submit"
			name="wa-locator-submit"
			id="wa-locator-submit"
		>
			Search
		</button>
		<div id="wa-error-msg">
			<span class="messages"></span>
			<div class="close">
				<i class="fa-solid fa-xmark"></i>
			</div>
		</div>
	`;

	map.controls[google.maps.ControlPosition.TOP_LEFT].push( addressLocatorForm );

	const addressInput = addressLocatorForm.querySelector( '#wa-locator-address' );

	addressLocatorForm.addEventListener( 'submit', ( e ) =>
	{
		e.preventDefault();

		if (addressInput instanceof HTMLInputElement) {
			locateAddress( addressInput.value, mapData, map );
		}
	} );

	const errorMsgElem = addressLocatorForm.querySelector( '#wa-error-msg' );
	const closeBtn     = addressLocatorForm.querySelector( '#wa-error-msg .close' );


	if (closeBtn instanceof HTMLElement && errorMsgElem instanceof HTMLElement) {
		closeBtn.addEventListener( 'click', () =>
		{
			hideMapErrorMsg();
		} );
	}

	if (addressInput instanceof HTMLInputElement && boundingBoxPoly) {
		attachMapPolygonClickEvent( map, boundingBoxPoly, ( latLng ) =>
		{
			addressInput.value = '';
			showAdviceInfoWindowForLatLng( mapData, map, latLng, false );
		} );
	}
};


/**
 *
 */
const showAdviceInfoWindowForLatLng = (
	mapData: WateringAdviceMapData,
	map: google.maps.Map,
	latLng: LatLng,
	recenter: boolean
) =>
{
	const lat                   = latLng.lat();
	const lng                   = latLng.lng();
	const mapWateringAdvicePath = '/api/map/get-advice-for-coords/';
	const url                   = wateringAdviceApiUrl + mapWateringAdvicePath + '?lat=' + lat + '&lng=' + lng + '&mapId=' + mapData.map.id;
	let infoHtml                = '';

	fetch( url )
		.then( response => response.json() )
		.then( response =>
		{
			if (response.success && addressLocatorInfoWindow) {

				if (recenter) {
					map.setCenter( latLng );
				}

				infoHtml += `
					<div id="wa-info-window">
						<h2>Watering Advice:</h2>
						<p>
							<strong>${response.wateringAmount}</strong> inches
						</p>
					</div>
				`;

				addressLocatorInfoWindow.setContent( infoHtml );
				addressLocatorInfoWindow.setPosition( latLng );

				addressLocatorInfoWindow.open( map );
			}
		} );
};


/**
 *
 */
const attachMapPolygonClickEvent = (
	map: google.maps.Map,
	polygon: google.maps.Polygon,
	onClickFunction: ( latLng: LatLng ) => any
) =>
{
	let uid     = uuidv4();
	let tracker = { isClick: false };

	clickTrackers[uid] = tracker;

	polygon.addListener( 'mousedown', () => tracker.isClick = true );
	window.addEventListener( 'touchmove', () => tracker.isClick = false )
	map.addListener( 'dragstart', () => tracker.isClick = false );
	polygon.addListener( 'mouseup', ( e: GoogleMapsMouseupEvent ) =>
	{
		if (tracker.isClick && e.latLng) {
			onClickFunction( e.latLng );
		}
	} );
};


/**
 *
 */
const locateAddress = ( address: string, mapData: WateringAdviceMapData, map: google.maps.Map ) =>
{
	const geocoder                = new google.maps.Geocoder();
	const info: GoogleGeocodeInfo = {
		success: true,
		errors:  [],
		center:  null,
	};
	const geocodeOptions          = {
		address: address.trim(),
		bounds:  initialBounds
	};

	hideMapErrorMsg();

	if (addressLocatorInfoWindow) {
		addressLocatorInfoWindow.close();
	}

	if (geocodeOptions.address.length < 2) {
		return;
	}

	// noinspection JSIgnoredPromiseFromCall
	geocoder.geocode( geocodeOptions, ( results, status ) =>
	{
		if (status === google.maps.GeocoderStatus.OK && results) {

			info.center = results[0].geometry.location;

			if (!initialBounds || !initialBounds.contains( info.center )) {
				showMapErrorMsg( 'This address is outside of our watering advice area.' );
				return;
			}

			showAdviceInfoWindowForLatLng( mapData, map, info.center, true );

		} else {
			info.success = false;
			info.errors.push( 'Unable to locate address' );
		}

		if (!info.success) {
			showMapErrorMsg( info.errors.join( '<br />' ) );
		}
	} );
};


/**
 *
 */
const showMapErrorMsg = ( msg: string ) =>
{
	const containerElem = document.querySelector( '#wa-error-msg' );
	const messagesElem  = document.querySelector( '#wa-error-msg .messages' );

	if (containerElem instanceof HTMLElement && messagesElem instanceof HTMLElement) {
		messagesElem.innerHTML      = msg;
		containerElem.style.display = 'block';
	}
};


/**
 *
 */
const hideMapErrorMsg = () =>
{
	const containerElem = document.querySelector( '#wa-error-msg' );

	if (containerElem instanceof HTMLElement) {
		containerElem.style.display = 'none';
	}
};


/**
 *
 */
const setupWaterViewRadios = ( mapData: WateringAdviceMapData, map: google.maps.Map ) =>
{
	const radios = document.querySelectorAll( '#water-key-switch [type="radio"]' );

	radios.forEach( radio =>
	{
		if (radio instanceof HTMLInputElement) {
			radio.addEventListener( 'change', () =>
			{
				showSelectedWaterView( mapData, map );
			} );
		}
	} );
};


/**
 *
 */
const showSelectedWaterView = ( mapData: WateringAdviceMapData, map: google.maps.Map ) =>
{
	const waterViews            = ['advice', 'rainfall'];
	const svgs                  = {
		advice:   mapData.map.svg,
		rainfall: mapData.map.rainfallSvg,
	};
	const checkedWaterViewInput = document.querySelector( '#water-key-switch [name="water-view"]:checked' );
	const colorLegendHeadline   = document.querySelector( '#color-legend-headline' );
	const waterKey              = document.querySelector( '#water-key' );

	if (checkedWaterViewInput instanceof HTMLInputElement) {

		let waterView = checkedWaterViewInput.value;

		// make sure waterView is valid

		if (waterViews.indexOf( waterView ) === -1) {
			waterView = waterViews[0];
		}

		// overlay image

		overlayMapImage( map, svgs[waterView] );


		// water view key

		if (colorLegendHeadline instanceof HTMLElement) {

			if (waterView === 'advice') {
				colorLegendHeadline.innerHTML = 'Watering Need Per Area:';
			} else if (waterView === 'rainfall') {
				colorLegendHeadline.innerHTML = 'Past 7-Day Rainfall Total:';
			}
		}

		if (waterKey instanceof HTMLElement && mapData.legendItems) {

			let keyHtml = `<ul class="water-key ${waterView}">`;

			mapData.legendItems[waterView].forEach( ( item: WateringAdviceLegendItem ) =>
			{
				const fill   = 'rgba(' + item.color.join( ',' ) + ')';
				const stroke = waterView === 'advice' && item.inches === 0 ? '#000' : '';

				keyHtml += `
					<li>
						<span class="key">
							${getLegendKeyIcon( waterView, fill, stroke )}
						</span>
						<span class="value">${item.label}</span>
					</li>
				`;
			} );

			keyHtml += '</ul>';

			waterKey.innerHTML = keyHtml;
		}
	}
};


const overlayMapImage = ( map: google.maps.Map, image: string ) =>
{
	if (!initialBounds) {
		return;
	}


	/**
	 *
	 */
	class WaterOverlay extends google.maps.OverlayView
	{
		private bounds: google.maps.LatLngBounds;
		private readonly image: string;
		private div?: HTMLElement;


		constructor( bounds: google.maps.LatLngBounds, image: string )
		{
			super();

			this.bounds = bounds;
			this.image  = image;
		}


		onAdd()
		{
			this.div                   = document.createElement( "div" );
			this.div.style.borderStyle = "none";
			this.div.style.borderWidth = "0px";
			this.div.style.position    = "absolute";

			const temp = document.createElement( 'div' );

			temp.innerHTML = this.image;

			const imageElem = temp.firstElementChild;

			if (imageElem instanceof SVGSVGElement) {

				imageElem.style.width    = '100%';
				imageElem.style.height   = '100%';
				imageElem.style.position = 'absolute';
				imageElem.style.opacity  = '0.75';
				this.div.appendChild( imageElem );

				const panes = this.getPanes()!;

				panes.overlayLayer.appendChild( this.div );
			}
		}


		draw()
		{
			const overlayProjection = this.getProjection();
			const sw                = overlayProjection.fromLatLngToDivPixel(
				this.bounds.getSouthWest()
			)!;
			const ne                = overlayProjection.fromLatLngToDivPixel(
				this.bounds.getNorthEast()
			)!;

			if (this.div) {
				this.div.style.left   = sw.x + "px";
				this.div.style.top    = ne.y + "px";
				this.div.style.width  = ne.x - sw.x + "px";
				this.div.style.height = sw.y - ne.y + "px";
			}
		}


		onRemove()
		{
			if (this.div) {
				(this.div.parentNode as HTMLElement).removeChild( this.div );
				delete this.div;
			}
		}
	}


	mapOverlays.forEach( overlay =>
	{
		overlay.setMap( null );
	} );

	const overlay: WaterOverlay = new WaterOverlay( initialBounds, image );

	overlay.setMap( map );

	mapOverlays.push( overlay );
};


/**
 *
 */
const fetchMapData = (): Promise<WateringAdviceMapData> =>
{
	return new Promise( ( resolve, reject ) =>
	{
		const wateringAdviceApiUrl = getAppConfigVar( 'wateringAdviceApiUrl' );
		const mapDataPath          = '/api/map/get-data/';
		const url                  = wateringAdviceApiUrl + mapDataPath + window.location.search;

		if (typeof wateringAdviceApiUrl !== 'string') {
			reject();
		}

		fetch( url )
			.then( response => response.json() )
			.then( ( response: WateringAdviceMapData ) =>
			{
				const legendItems: WateringAdviceLegendItems = {
					advice:   [],
					rainfall: [],
				};

				// create array of advice legend items

				Object.keys( response.map.waterAdviceParams ).forEach( ( handle: string ) =>
				{
					const param = response.map.waterAdviceParams[handle];

					// ignore if watering amount is over the maximum specified for the map

					if (param.wateringAmount <= response.map.maxWateringAmountUser) {

						legendItems.advice.push( {
							color:  param.rgba,
							inches: param.wateringAmount,
							label:  param.wateringAmount === 0
									? 'No Watering'
									: param.wateringAmount.toString() + (param.wateringAmount === 1 ? ' inch' : ' inches')
						} );

					}
				} );

				// for consistency and to make layouts easier, always use a max of 4 legend items.

				if (legendItems.advice.length > 3) {
					legendItems.advice.shift();
				}

				// sort advice legend items

				legendItems.advice.sort( function ( a, b )
				{
					return a.inches > b.inches ? 1 : -1;
				} );

				// create array of rainfall legend items

				response.rainfallKey.forEach( item =>
				{
					const colorObj = hexToRgbaObject( item.color );

					legendItems.rainfall.push( {
						color:  Object.values( colorObj ),
						inches: item.inches,
						label:  item.label
					} );
				} );

				response.legendItems = legendItems;

				resolve( response );
			} );
	} );
};


/**
 *
 */
const populateText = ( mapData: WateringAdviceMapData ) =>
{
	const amountElem   = document.querySelector( '#advice-amount-range' );
	const overviewElem = document.querySelector( '#advice-overview' );
	const waterKeyElem = document.querySelector( '#water-key' );
	const taglineElem  = document.querySelector( '.hero .tagline' );

	if (taglineElem instanceof HTMLElement) {

		taglineElem.innerText = `${mapData.map.startDateFormatted} - ${mapData.map.endDateFormatted}`;

		setTimeout( () =>
		{
			if (taglineElem) {
				taglineElem.classList.remove( 'hidden' );
			}
		}, 50 );
	}

	// advice amount

	if (amountElem instanceof HTMLElement) {

		let adviceRangeHtml = mapData.map.minWatering.toFixed( 2 ) + '"';

		if (mapData.map.minWatering !== mapData.map.maxWatering) {
			adviceRangeHtml += ' - ' + mapData.map.maxWatering.toFixed( 2 ) + '"';
		}

		amountElem.innerHTML = adviceRangeHtml;
	}

	// overview paragraph

	if (overviewElem instanceof HTMLElement) {
		overviewElem.innerHTML = mapData.map.overviewParagraph;
	}

	// water amount key

	if (waterKeyElem instanceof HTMLElement) {

		const waterView = getWaterView();
		let keyHtml     = '<ul class="water-key ' + waterView + '">';

		if (mapData.legendItems) {

			mapData.legendItems[waterView].forEach( ( item: WateringAdviceLegendItem ) =>
			{
				const fill   = 'rgba(' + item.color.join( ',' ) + ')';
				const stroke = waterView === 'advice' && item.inches === 0 ? '#000' : '';

				keyHtml += `
					<li>
						<span class="key">
							${getLegendKeyIcon( waterView, fill, stroke )}
						</span>
						<span class="value">
							${item.label}
						</span>
					</li>
				`;
			} );
		}

		keyHtml += '</ul>';

		waterKeyElem.innerHTML = keyHtml;
	}

	// sprinkler times

	Object.keys( mapData.map.waterAdviceParams ).forEach( handle =>
	{
		const p: WaterAdviceParam = mapData.map.waterAdviceParams[handle];

		if (p.sprinklerTimes && p.wateringAmount > 0) {

			Object.keys( p.sprinklerTimes ).forEach( type =>
			{
				if (p.sprinklerTimes) {

					const sprinklerTime: SprinklerTime = p.sprinklerTimes[type];
					const tbody                        = document.querySelector( `[data-sprinkler-type="${type}"] tbody` );

					if (tbody instanceof HTMLElement) {

						// don't show sprinkler times for amounts greater than the maximum watering amount

						if (p.wateringAmount <= mapData.map.maxWatering) {

							const rowHTML = `
								<tr>
									<td>${p.wateringAmount.toFixed( 2 )} inches</td>
									<td>${sprinklerTime.zoneRuntime} min</td>
									<td><span class="cycles-per-zone">${sprinklerTime.cyclesPerZone}</span></td>
									<td>${sprinklerTime.runtimePerZone}  min</td>
								</tr>
							`;

							tbody.insertAdjacentHTML( 'beforeend', rowHTML );
						}
					}
				}
			} );
		}
	} );
};


/**
 *
 */
const getWaterView = (): string =>
{
	const elem = document.querySelector( '#water-key-switch [name="water-view"]:checked' );

	if (elem instanceof HTMLInputElement) {
		return elem.value;
	}

	return 'advice';
};


/**
 *
 */
const getLegendKeyIcon = ( type: string, fill: string, stroke: string ): string =>
{
	let svg = '';

	if (type === 'advice') {

		svg += '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"';
		svg += '	width="83.311px" height="105.977px" viewBox="0 0 83.311 105.977" enable-background="new 0 0 83.311 105.977" xml:space="preserve">';
		svg += '	<g>';
		svg += '		<path fill="' + fill + '"';
		svg += (stroke ? ' stroke="' + stroke + '" stroke-width="2"' : '');
		svg += '			d="M42.072,1h-0.808 C37.125,14.423,1,31.414,1,64.321c0,22.453,18.202,40.655,40.655,40.655s40.655-18.202,40.655-40.655';
		svg += '			C82.311,31.355,46.346,13.98,42.072,1z"/>';
		svg += '	</g>';
		svg += '</svg>';

	} else if (type === 'rainfall') {

		svg += '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"';
		svg += '	width="64px" height="64px" viewBox="0 0 64 64" enable-background="new 0 0 64 64" xml:space="preserve">';
		svg += '	<rect x="1.5" y="1.5" fill="' + fill + '" stroke="' + stroke + '" ';
		svg += '		stroke-width="3.1017" stroke-miterlimit="10" width="61" height="61"/>';
		svg += '</svg>';
	}

	return svg;
};


/**
 *
 */
const getMapStyles = () =>
{
	return [
		{
			"featureType": "landscape.natural.landcover",
			"elementType": "geometry.fill",
			"stylers":     [
				{
					"visibility": "off"
				}
			]
		}
	];
};


/**
 *
 */
export default setupWeeklyWateringAdvice;